import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import {
  ACCOUNTING_METHOD_TYPES,
  ACCOUNTING_METHODS_OPTIONS,
} from "constants/bookkeeping";
import { MONTH_FORMAT, YYYY_MM_DD } from "constants/date";
import { CLOSING_STATUS } from "constants/financialClosing";
import dayjs from "dayjs";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useHistory, useParams } from "react-router-dom";
import { useAddFinancialClosingMutation } from "store/apis/financialClosing";
import { useGetTaskDataQuery } from "store/apis/taskTags";
import { US_BOOKKEEPING_SUBSCRIPTION_PARENT } from "types/Models/services";
import { TaskStateType } from "types/Models/task";
import { BackendError } from "types/utils/error";
import * as Yup from "yup";

type ClosingFormValues = {
  from_date: string;
  to_date: string;
  accounting_method: ACCOUNTING_METHOD_TYPES;
};

export const AddFinancialClosing = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const history = useHistory();
  const [addFinancial, { isLoading: isAdding }] =
    useAddFinancialClosingMutation();
  const { alertToast } = useToast();
  const addDateModal = useModal();
  const { isCustomer } = useRoleBasedView();

  const {
    data: taskData,
    isLoading,
    isSuccess,
  } = useGetTaskDataQuery({ uuid: taskId }, { skip: !taskId });

  const {
    entity,
    group_uuid,
    from_date,
    to_date,
    accounting_method,
    state,
    base_task_key,
    financial_closing,
  } = taskData || {};

  const entityId = entity?.uuid;

  const onAddFinancial = async ({
    from_date,
    to_date,
    accounting_method,
  }: ClosingFormValues) => {
    try {
      if (!entityId || !accounting_method) {
        alertToast({
          message: "Accounting method is missing.",
        });
        return;
      }

      const { uuid } = await addFinancial({
        entityId,
        payload: {
          accounting_method,
          start_date: from_date
            ? dayjs(from_date).startOf("month").format(YYYY_MM_DD)
            : "",
          end_date: to_date
            ? dayjs(to_date).endOf("month").format(YYYY_MM_DD)
            : "",
          task_id: taskId,
        },
      }).unwrap();
      addDateModal.close();
      history.push(
        `/books/financial-closing/${uuid}?company=${group_uuid}&entity=${entityId}`
      );
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  const onView = () => {
    const entityId = entity?.uuid;
    history.push(
      `/books/financial-closing/${financial_closing?.uuid}?company=${group_uuid}&entity=${entityId}`
    );
  };

  if (financial_closing) {
    return (
      <Button
        customType="primary_outlined"
        type="button"
        size="small"
        onClick={onView}
      >
        View financials
      </Button>
    );
  }

  if (isCustomer || base_task_key === US_BOOKKEEPING_SUBSCRIPTION_PARENT) {
    return null;
  }

  return (
    <Async.Root
      {...{
        isLoading,
        isSuccess,
        isEmpty:
          !taskData || (state as TaskStateType)?.type !== "FILING_IN_PROGRESS",
      }}
      loaderType="secondary"
    >
      <Async.Empty>
        <></>
      </Async.Empty>
      <Async.Success>
        {from_date && to_date && accounting_method ? (
          <Button
            customType="primary_outlined"
            size="small"
            onClick={() =>
              onAddFinancial({
                from_date,
                to_date,
                accounting_method: accounting_method!,
              })
            }
            disabled={isAdding}
            isLoading={isAdding}
            type="button"
          >
            Add financials
          </Button>
        ) : (
          <Modal.Root
            open={addDateModal.isOpen}
            onOpenChange={addDateModal.toggle}
          >
            <Modal.Trigger asChild>
              <Button customType="primary_outlined" size="small" type="button">
                Add financials
              </Button>
            </Modal.Trigger>
            <Modal.Content className="t-max-w-md">
              <FormikForm
                initialValues={{
                  from_date: from_date || "",
                  to_date: from_date || "",
                  accounting_method: accounting_method || "",
                }}
                onSubmit={(values) => {
                  onAddFinancial(values as ClosingFormValues);
                }}
                validationSchema={Yup.object().shape({
                  accounting_method: Yup.string().required(
                    "Accounting method is required"
                  ),
                  from_date: Yup.date()
                    .max(
                      Yup.ref("to_date"),
                      "From date cannot be after to date"
                    )
                    .required("From date is required"),
                  to_date: Yup.date()
                    .min(Yup.ref("from_date"), "To date cannot be before")
                    .required("To date is required"),
                })}
              >
                {({ isValid, isSubmitting, submitForm }) => (
                  <>
                    <Modal.Header>
                      <Modal.Title>Add financial closing</Modal.Title>
                      <Modal.Close />
                    </Modal.Header>
                    <Modal.Body className="t-flex t-flex-col t-gap-4">
                      <Combobox
                        withForm
                        name="accounting_method"
                        label="Accounting method"
                        options={ACCOUNTING_METHODS_OPTIONS}
                        placeholder="Select accounting method"
                        required
                        defaultValue={
                          ACCOUNTING_METHODS_OPTIONS.find(
                            (option) => option.value === accounting_method
                          ) || null
                        }
                      />
                      <DateField
                        name="from_date"
                        dateProps={{
                          label: "From date",
                          placeholder: "Select",
                          required: true,
                          showMonthYearPicker: true,
                          dateFormat: MONTH_FORMAT,
                        }}
                      />
                      <DateField
                        name="to_date"
                        dateProps={{
                          label: "To date",
                          placeholder: "Select",
                          required: true,
                          showMonthYearPicker: true,
                          dateFormat: MONTH_FORMAT,
                        }}
                      />
                    </Modal.Body>
                    <Modal.FooterButtonGroup>
                      <Modal.RawClose asChild>
                        <Button disabled={isSubmitting}>Cancel</Button>
                      </Modal.RawClose>
                      <Button
                        customType="primary"
                        disabled={isSubmitting || !isValid}
                        isLoading={isSubmitting}
                        onClick={submitForm}
                      >
                        Add financials
                      </Button>
                    </Modal.FooterButtonGroup>
                  </>
                )}
              </FormikForm>
            </Modal.Content>
          </Modal.Root>
        )}
      </Async.Success>
    </Async.Root>
  );
};

export const ReviewFinancialClosing = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const history = useHistory();

  const {
    data: taskData,
    isLoading,
    isSuccess,
  } = useGetTaskDataQuery({ uuid: taskId }, { skip: !taskId });
  const { group_uuid, entity, financial_closing, base_task_key } =
    taskData || {};

  const onReview = () => {
    const entityId = entity?.uuid;

    history.push(
      `/books/financial-closing/${financial_closing?.uuid}?company=${group_uuid}&entity=${entityId}`
    );
  };

  const isPushedForReview =
    financial_closing?.status === CLOSING_STATUS.REVIEW_PENDING;

  if (base_task_key === US_BOOKKEEPING_SUBSCRIPTION_PARENT) {
    return null;
  }
  return (
    <Async.Root
      {...{
        isLoading,
        isSuccess,
        isEmpty: !taskData || !isPushedForReview || !financial_closing,
      }}
      loaderType="secondary"
    >
      <Async.Empty>
        <></>
      </Async.Empty>
      <Async.Success>
        <Button
          customType="primary_outlined"
          size="small"
          type="button"
          onClick={onReview}
        >
          Review financials
        </Button>
      </Async.Success>
    </Async.Root>
  );
};

export const ViewFinancialClosing = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const history = useHistory();

  const {
    data: taskData,
    isLoading,
    isSuccess,
  } = useGetTaskDataQuery({ uuid: taskId }, { skip: !taskId });
  const { group_uuid, entity, financial_closing, base_task_key } =
    taskData || {};

  const onReview = () => {
    const entityId = entity?.uuid;

    history.push(
      `/books/financial-closing/${financial_closing?.uuid}?company=${group_uuid}&entity=${entityId}`
    );
  };

  const isCompleted = financial_closing?.status === CLOSING_STATUS.COMPLETED;

  if (base_task_key === US_BOOKKEEPING_SUBSCRIPTION_PARENT) {
    return null;
  }

  return (
    <Async.Root
      {...{
        isLoading,
        isSuccess,
        isEmpty: !taskData || !isCompleted || !financial_closing,
      }}
      loaderType="secondary"
    >
      <Async.Empty>
        <></>
      </Async.Empty>
      <Async.Success>
        <Button
          customType="primary_outlined"
          type="button"
          size="small"
          onClick={onReview}
        >
          View financials
        </Button>
      </Async.Success>
    </Async.Root>
  );
};
